// ServiceCreditModal — records, amends or removes the service credit for one
// contract-month.
//
// Credits are RECORDED, never derived. A KPI's `serviceCreditFormula` is
// display-only text the system never evaluates, so the amount here is a
// person's assessment and the working beside it is the only evidence of how
// they reached it — which is why the note is required even for a waiver.
//
// Every write carries the version the author read. A stale one answers 409,
// and the draft stays on screen: the reader has to decide whether to overwrite
// what landed, and losing a figure they just worked out to a silent retry is
// not acceptable for a monetary record. The conflict also refreshes the
// contract's report queries and says where to look, so the author is not left
// holding a rejected figure with no way to see the one that won.
//
// Removing a credit takes a reason. The record is a decision about money, so
// the audit trail has to say why it stopped being true.
//
// Both free-text fields cap at the server's own limit rather than accepting
// an over-long value and refusing it on submit, which is why there is no
// length check below: the field cannot hold one.

import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "../../../../common/Modal";
import Button from "../../../../common/Button";
import Alert from "../../../../common/Alert";
import CurrencyInput from "../../../../common/CurrencyInput";
import useSeededFormState from "../../../../../hooks/useSeededFormState";
import {
  useDeleteServiceCredit,
  useUpsertServiceCredit,
} from "../../../../../hooks/useContractReport";
import { parseNumericText } from "../../../../../utils/numericText";
import { formatMonthYear } from "../../../../../utils/formatShortDate";
import type { ApiError } from "../../../../../services/api";
import {
  SERVICE_CREDIT_NOTE_MAX,
  type ServiceCredit,
} from "../../../../../types/report";

interface CreditDraft {
  amount: string;
  note: string;
}

/** Which control a validation message belongs to, so the message can be
 *  announced against the field the reader has to fix rather than floating
 *  free at the bottom of the form. */
type CreditField = "amount" | "note";

const FORM_ERROR_ID = "credit-form-error";
const REASON_ERROR_ID = "credit-reason-error";

const CONFLICT_HINT = "Cancel, then reopen to see the figure that landed.";

export interface ServiceCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  /** `YYYY-MM`. A credit belongs to a calendar month, independent of any
   *  KPI's own period strings, which are free text and cadence-mixed. */
  month: string;
  /** The recorded credit for this month, or null to record the first. */
  credit: ServiceCredit | null;
}

export default function ServiceCreditModal({
  isOpen,
  onClose,
  contractId,
  month,
  credit,
}: ServiceCreditModalProps) {
  const [fieldError, setFieldError] = useState<{
    field: CreditField;
    message: string;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  // Seeded on open rather than at mount: a modal rendered eagerly would
  // otherwise capture whichever month was selected first and show its figures
  // against every later one.
  const [draft, setDraft] = useSeededFormState<CreditDraft>(
    isOpen,
    `${month}:${credit?.version ?? 0}`,
    () => ({
      amount: credit ? String(credit.amount) : "",
      note: credit?.note ?? "",
    }),
    () => {
      setFieldError(null);
      setSaveError(null);
      setConfirmingDelete(false);
      setReason("");
      setReasonError(null);
    },
  );

  const upsert = useUpsertServiceCredit();
  const remove = useDeleteServiceCredit();
  const queryClient = useQueryClient();

  const monthLabel = formatMonthYear(`${month}-01`);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaveError(null);

    const amount = parseNumericText(draft.amount);
    if (amount === null) {
      setFieldError({
        field: "amount",
        message: "Enter an amount, or waive the credit.",
      });
      return;
    }
    if (amount < 0) {
      setFieldError({
        field: "amount",
        message: "A credit cannot be negative.",
      });
      return;
    }
    const note = draft.note.trim();
    if (note === "") {
      setFieldError({
        field: "note",
        message: "Add a short working so the amount can be checked later.",
      });
      return;
    }
    setFieldError(null);

    try {
      await upsert.mutateAsync({
        contractId,
        month,
        input: { amount, note, version: credit?.version ?? 0 },
      });
      onClose();
    } catch (err) {
      // apiRequest rejects with an ApiError for every outcome including a
      // network failure, so there is no other shape to narrow against.
      const error = err as ApiError;
      if (error.status === 409) {
        // Refresh what the report surfaces hold so the figure that actually
        // landed is on screen the moment this form closes.
        queryClient.invalidateQueries({ queryKey: ["report", contractId] });
        setSaveError(`${error.message} ${CONFLICT_HINT}`);
        return;
      }
      setSaveError(error.message);
    }
  }

  async function handleDelete() {
    const stated = reason.trim();
    if (stated === "") {
      setReasonError("Say why this credit is being removed.");
      return;
    }
    setReasonError(null);
    setSaveError(null);

    try {
      await remove.mutateAsync({
        contractId,
        month,
        input: { version: credit!.version, reason: stated },
      });
      onClose();
    } catch (err) {
      const error = err as ApiError;
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["report", contractId] });
        setSaveError(`${error.message} ${CONFLICT_HINT}`);
        return;
      }
      setSaveError(error.message);
    }
  }

  const busy = upsert.isPending || remove.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={credit ? `Service credit · ${monthLabel}` : `Record a service credit · ${monthLabel}`}
      maxWidth="sm:max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-body-sm text-slate-600 dark:text-slate-400">
          Credits are recorded against the month, not against a single KPI — a
          credit is usually assessed across the month&apos;s failures together.
        </p>

        <div>
          <label htmlFor="credit-amount" className="label">
            Amount (ex-GST)
          </label>
          <div className="flex items-center gap-2">
            {/* The wrapper carries the flex sizing: CurrencyInput forwards
                className to its inner input, which sits inside its own
                adornment wrapper and would not stretch the row. */}
            <div className="flex-1">
              <CurrencyInput
                id="credit-amount"
                value={draft.amount}
                onChange={(amount) => setDraft((d) => ({ ...d, amount }))}
                disabled={busy}
                ariaInvalid={fieldError?.field === "amount"}
                ariaDescribedBy={
                  fieldError?.field === "amount" ? FORM_ERROR_ID : undefined
                }
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setDraft((d) => ({ ...d, amount: "0" }))}
            >
              Waive
            </Button>
          </div>
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            A waived credit is recorded as $0.00 with its working, which is
            not the same as recording nothing at all.
          </p>
        </div>

        <div>
          <label htmlFor="credit-note" className="label">
            Working
          </label>
          <textarea
            id="credit-note"
            className="input"
            rows={3}
            maxLength={SERVICE_CREDIT_NOTE_MAX}
            disabled={busy}
            aria-invalid={fieldError?.field === "note" ? true : undefined}
            aria-describedby={
              fieldError?.field === "note" ? FORM_ERROR_ID : undefined
            }
            value={draft.note}
            onChange={(event) =>
              setDraft((d) => ({ ...d, note: event.target.value }))
            }
            placeholder="e.g. 3 failures → 10% of the July invoice $58,900.00"
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {draft.note.trim().length} of {SERVICE_CREDIT_NOTE_MAX} characters
          </p>
        </div>

        {fieldError && (
          <Alert variant="error" id={FORM_ERROR_ID}>
            {fieldError.message}
          </Alert>
        )}
        {saveError && <Alert variant="error">{saveError}</Alert>}

        {confirmingDelete ? (
          <div
            data-testid="credit-delete-confirm"
            className="space-y-3 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30"
          >
            <label htmlFor="credit-delete-reason" className="label">
              Reason for removing this credit
            </label>
            <textarea
              id="credit-delete-reason"
              className="input"
              rows={2}
              maxLength={SERVICE_CREDIT_NOTE_MAX}
              disabled={busy}
              aria-invalid={reasonError !== null ? true : undefined}
              aria-describedby={
                reasonError !== null ? REASON_ERROR_ID : undefined
              }
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {reasonError && (
              <Alert variant="error" id={REASON_ERROR_ID}>
                {reasonError}
              </Alert>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setConfirmingDelete(false);
                  setReasonError(null);
                }}
              >
                Keep credit
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={busy}
                onClick={handleDelete}
              >
                Remove credit
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            {credit ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  // A failed save and the removal confirm are two different
                  // conversations; stacking both alerts buries whichever one
                  // the reader is answering.
                  setSaveError(null);
                  setFieldError(null);
                  setConfirmingDelete(true);
                }}
              >
                Remove credit
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={busy}>
                {credit ? "Save credit" : "Record credit"}
              </Button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
